import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import RoutineNotes from "@/components/RoutineNotes";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PHASE_LABEL = {
  skin_resilience: "Skin Resilience",
  making_actives_count: "Making Actives Count",
  precision_corrections: "Precision Corrections",
};
const SKIN_LABEL = { dry_sensitive: "Dry / Sensitive", normal_combo: "Normal / Combination", oily: "Oily" };

const TABS = [
  { key: "skin_resilience", label: "Skin Resilience" },
  { key: "making_actives_count", label: "Making Actives Count" },
  { key: "precision_corrections", label: "Precision Corrections" },
];

export default function Protocol() {
  const { user } = useAuth();
  const [all, setAll] = useState(null);
  const [tab, setTab] = useState(user?.current_phase || "skin_resilience");
  const [showOriginal, setShowOriginal] = useState(false);
  const vitcReady = !!user?.vitamin_c_ready_at;

  const load = (original) =>
    api.get(`/protocols/all${original ? "?original=true" : ""}`).then((r) => setAll(r.data));

  useEffect(() => {
    load(false);
  }, []);

  const setOriginal = (val) => {
    if (val === showOriginal) return;
    setShowOriginal(val);
    load(val);
  };

  const resetSwap = async (slotKey) => {
    try {
      await api.delete(`/swaps/${encodeURIComponent(slotKey)}`);
      load(showOriginal);
    } catch (e) {}
  };

  if (!all) return <><NavBar /><div className="max-w-6xl mx-auto px-6 lg:px-10 py-16"><div className="aa-eyebrow">Loading protocols…</div></div></>;

  const data = all[tab];

  return (
    <div className="App" data-testid="page-protocol">
      <NavBar />
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-4">My protocols · {SKIN_LABEL[user?.skin_type]}</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">Your AM & PM <span className="serif-italic">routine</span>.</h1>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-[var(--aa-elevated)]">
          {TABS.map((t) => {
            const isCurrent = all[t.key]?.is_current;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`protocol-tab-${t.key}`}
                className={`px-4 py-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === t.key ? "border-[var(--aa-text)] text-[var(--aa-text)]" : "border-transparent text-[var(--aa-text-3)] hover:text-[var(--aa-text)]"}`}
              >
                {t.label} {isCurrent && <span className="ml-2 aa-pill verdict-green text-[10px] px-2 py-0.5">Current</span>}
              </button>
            );
          })}
        </div>

        {tab !== "precision_corrections" && (
          <>
            {(all.has_swaps || showOriginal) && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span className="aa-eyebrow">Viewing:</span>
                <button
                  data-testid="protocol-toggle-mine"
                  onClick={() => setOriginal(false)}
                  className={`aa-pill px-4 py-2 text-sm ${showOriginal ? "hover:bg-[var(--aa-elevated)]" : "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]"}`}
                >My version</button>
                <button
                  data-testid="protocol-toggle-rey"
                  onClick={() => setOriginal(true)}
                  className={`aa-pill px-4 py-2 text-sm ${showOriginal ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}
                >Original Dr Rey protocol</button>
              </div>
            )}
            <div className="grid lg:grid-cols-2 gap-6 mt-10">
              <Block title="Morning" steps={data.am} testid="protocol-am" onReset={resetSwap} viewingOriginal={showOriginal} phase={tab} vitcReady={vitcReady} />
              <Block title="Evening" steps={data.pm} testid="protocol-pm" onReset={resetSwap} viewingOriginal={showOriginal} phase={tab} vitcReady={vitcReady} />
            </div>

            {data.purpose_process && Object.keys(data.purpose_process).length > 0 && (
              <div className="mt-12">
                <h2 className="font-display text-2xl mb-5">Purpose & Process</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  {Object.entries(data.purpose_process).map(([cat, pp], idx) => {
                    const tints = ["aa-card-warm", "aa-card-sage", "aa-card-blush", "aa-card-stone"];
                    const tint = tints[idx % tints.length];
                    return (
                    <div key={cat} className={`aa-card ${tint} p-6`} data-testid={`purpose-${cat}`}>
                      <div className="aa-eyebrow mb-2">{cat === "serum" ? "Hydrating Serum" : cat.replace("_", " ")}</div>
                      <div className="font-display text-lg mt-1 mb-3">Purpose</div>
                      <p className="text-sm text-[var(--aa-text-2)]">{pp.purpose}</p>
                      <div className="aa-divider my-4 opacity-60"></div>
                      <div className="font-display text-lg mb-2">Process</div>
                      <p className="text-sm text-[var(--aa-text-2)]">{pp.process}</p>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            <RoutineNotes />
          </>
        )}

        {tab === "precision_corrections" && <PrecisionCorrections data={data} />}
      </section>
    </div>
  );
}

function PrecisionCorrections({ data }) {
  return (
    <div className="mt-10 space-y-6">
      <p className="text-[var(--aa-text-2)] max-w-3xl">Targeted treatments for specific concerns. Most can be layered in during the Making Actives Count phase; exfoliators are held off until your retinoid is fully optimized.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {Object.entries(data.concerns).map(([key, c]) => (
          <div key={key} className="aa-card p-6" data-testid={`precision-${key}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
              <div className="font-display text-xl">{c.title}</div>
              {!c.available_during_mac && <span className="aa-pill verdict-yellow text-xs">Gated</span>}
            </div>
            <p className="text-sm text-[var(--aa-text-2)] mb-4">{c.summary}</p>
            <ul className="space-y-3">
              {c.options.map((o, i) => (
                <li key={i} className="border-t border-[var(--aa-elevated)] pt-3">
                  <div className="aa-eyebrow mb-1">{o.label}</div>
                  {o.url ? (
                    <a href={o.url} target="_blank" rel="noopener noreferrer" className="font-display hover:underline">{o.product}</a>
                  ) : (
                    <div className="font-display">{o.product}</div>
                  )}
                  <div className="text-sm text-[var(--aa-text-2)] mt-1 leading-relaxed">{o.when}</div>
                </li>
              ))}
            </ul>
            {c.gated_note && <div className="mt-4 text-sm italic text-[var(--aa-text-3)] leading-relaxed">{c.gated_note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Block({ title, steps, testid, onReset, viewingOriginal, phase, vitcReady }) {
  const isMAC = phase === "making_actives_count";
  const tint = title === "Morning" ? "aa-card-warm" : "aa-card-stone";
  return (
    <div className={`aa-card ${tint} p-7`} data-testid={testid}>
      <div className="aa-eyebrow mb-4">{title}</div>
      <ol className="space-y-5">
        {steps.map((s, i) => {
          const stepLower = (s.step || "").toLowerCase();
          const isVitC = isMAC && stepLower === "vitamin c";
          return (
          <li key={i} className="flex gap-4 items-start border-b border-[var(--aa-elevated)] last:border-0 pb-4 last:pb-0">
            <span className="aa-eyebrow w-7 mt-1">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm text-[var(--aa-text-3)]">{s.step}</div>
                {s.swapped && !viewingOriginal && (
                  <span className="aa-pill verdict-green text-xs px-2 py-0.5">Swapped</span>
                )}
                {isVitC && vitcReady && (
                  <span className="aa-pill verdict-green text-xs px-2 py-0.5" data-testid="vitc-ready-badge">Vitamin C ready ✓</span>
                )}
              </div>
              {s.url ? (
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="font-display text-lg mt-0.5 hover:underline underline-offset-4">{s.product}</a>
              ) : (
                <div className="font-display text-lg mt-0.5">{s.product}</div>
              )}
              {s.swapped && !viewingOriginal && (
                <div className="text-sm text-[var(--aa-text-3)] mt-1 leading-relaxed">
                  Replaces Dr Rey's: <span className="italic">{s.swap_original_product}</span>
                  {onReset && (
                    <button
                      data-testid={`reset-swap-${s.swap_slot_key}`}
                      onClick={() => onReset(s.swap_slot_key)}
                      className="ml-2 underline underline-offset-2 hover:text-[var(--aa-text)]"
                    >Reset</button>
                  )}
                </div>
              )}
              {s.alt && (
                <div className="text-sm text-[var(--aa-text-2)] mt-1 leading-relaxed">
                  {s.alt_label || "Alternative"}: {s.alt_url ? <a href={s.alt_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{s.alt}</a> : s.alt}
                </div>
              )}
              {s.note && <div className="text-sm italic text-[var(--aa-text-3)] mt-1 leading-relaxed">{s.note}</div>}
              {isVitC && !vitcReady && (
                <Link
                  to="/vitamin-c-readiness"
                  data-testid="protocol-vitc-cta"
                  className="aa-cta-secondary mt-3 inline-flex"
                >
                  Ready for vitamin C? →
                </Link>
              )}
              {isVitC && vitcReady && (
                <Link
                  to="/vitamin-c-readiness"
                  data-testid="protocol-vitc-reassess"
                  className="text-xs text-[var(--aa-text-3)] underline underline-offset-4 hover:text-[var(--aa-text)] mt-2 inline-flex"
                >
                  Reassess vitamin C readiness
                </Link>
              )}
            </div>
          </li>
          );
        })}
      </ol>
    </div>
  );
}
